import { useMutation, useQueryClient } from '@tanstack/react-query';
import LocalAPI from 'utils/LocalAPI';
import { normalizeId } from '../utils/idNormalization';
import { collapseKeys } from './useCollapseState';

// Mutation configurations
const MUTATION_CONFIGS = {
  collapseCode: {
    apiMethod: LocalAPI.collapseCode.bind(LocalAPI),
    getDataMethod: LocalAPI.getCodeBook.bind(LocalAPI),
    queryKey: collapseKeys.codeBook,
    collapseValue: true
  },
  expandCode: {
    apiMethod: LocalAPI.expandCode.bind(LocalAPI),
    getDataMethod: LocalAPI.getCodeBook.bind(LocalAPI),
    queryKey: collapseKeys.codeBook,
    collapseValue: false
  },
  collapseCodeBar: {
    apiMethod: LocalAPI.collapseCodeBar.bind(LocalAPI),
    getDataMethod: LocalAPI.getCodeBar.bind(LocalAPI),
    queryKey: collapseKeys.codeBar,
    collapseValue: true
  },
  expandCodeBar: {
    apiMethod: LocalAPI.expandCodeBar.bind(LocalAPI),
    getDataMethod: LocalAPI.getCodeBar.bind(LocalAPI),
    queryKey: collapseKeys.codeBar,
    collapseValue: false
  }
};

/**
 * Creates a mutation configuration for collapse/expand actions
 * @param {Object} config - Configuration object
 * @param {Function} config.apiMethod - The LocalAPI method to call
 * @param {Function} config.getDataMethod - Method to fetch current data
 * @param {Function} config.queryKey - Function to get the query key
 * @param {boolean} config.collapseValue - Value to set (true for collapse, false for expand)
 * @param {string|number} projectId - Project ID
 * @param {string|number} normalizedProjectId - Normalized project ID
 * @param {Object} queryClient - React Query client
 */
function createCollapseMutation(config, projectId, normalizedProjectId, queryClient) {
  const { apiMethod, getDataMethod, queryKey, collapseValue } = config;
  
  return useMutation({
    mutationFn: ({ codeId }) => {
      apiMethod(projectId, codeId);
      return getDataMethod(projectId);
    },
    onMutate: async ({ codeId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: queryKey(normalizedProjectId) 
      });

      // Snapshot the previous value
      const previousState = queryClient.getQueryData(
        queryKey(normalizedProjectId)
      );

      // Optimistically update
      queryClient.setQueryData(
        queryKey(normalizedProjectId),
        (old = {}) => ({
          ...old,
          [codeId]: collapseValue
        })
      );

      return { previousState };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousState !== undefined) {
        queryClient.setQueryData(
          queryKey(normalizedProjectId),
          context.previousState
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ 
        queryKey: queryKey(normalizedProjectId) 
      });
    }
  });
}

/**
 * Hook that provides actions for collapsing and expanding codes
 * @param {string|number} projectId - The project ID
 * @returns {Object} Object with collapse/expand actions
 */
export function useCollapseActions(projectId) {
  const queryClient = useQueryClient();
  const normalizedProjectId = normalizeId(projectId);

  // Create all mutations using the factory
  const mutations = Object.entries(MUTATION_CONFIGS).reduce((acc, [key, config]) => {
    acc[key] = createCollapseMutation(config, projectId, normalizedProjectId, queryClient);
    return acc;
  }, {});

  return {
    // Code Book actions
    collapseCode: (codeId) => mutations.collapseCode.mutate({ codeId }),
    expandCode: (codeId) => mutations.expandCode.mutate({ codeId }),
    
    // Code Bar actions
    collapseCodeBar: (codeId) => mutations.collapseCodeBar.mutate({ codeId }),
    expandCodeBar: (codeId) => mutations.expandCodeBar.mutate({ codeId }),
    
    // Loading states
    isLoading: Object.values(mutations).some(mutation => mutation.isPending)
  };
}